import { useEffect, useRef, useState } from "react";

import {
  useMailVorlageLoeschen,
  useMailVorlagePflegen,
  useMailVorlageStandard,
  useMailVorlagen,
  type MailVorlage,
  type MailVorlageArt,
} from "../../hooks/useMail";
import { errorText } from "../../lib/invoices";

import "../../styles/bibliothek.css";

/**
 * Die Mailvorlagen — mehrere je Belegart, eine davon die Vorgabe. Seit
 * Migration 164 gemeinsam für beide Inhaber; persönlich ist nur die Signatur.
 *
 * Bis Migration 149 gab es genau eine je Inhaber und Belegart, und „Als
 * Vorlage übernehmen" hat sie jedes Mal überschrieben. Ein Erstangebot, ein
 * Nachfassen und ein Zahlungserinnerungs-Anschreiben nebeneinander zu haben
 * ging damit nicht (Benjamin, 16.09.2026).
 *
 * Bewusst dieselben Klassen wie die Textbaustein-Bibliothek: es ist derselbe
 * Vorgang — benannte Texte pflegen und einen als Standard markieren —, und
 * zwei Masken dafür wären zwei Bedienmuster für eine Sache.
 *
 * Gespeichert wird **je Vorlage**, nicht die ganze Liste: anders als bei den
 * Textbausteinen liegt hier jede Vorlage in einer eigenen Zeile, ein
 * Voll-Ersetzen gäbe es also nur als selbstgebauten Umweg.
 */

const ARTEN: { id: MailVorlageArt; titel: string }[] = [
  { id: "quote", titel: "Angebote" },
  { id: "invoice", titel: "Rechnungen" },
];

type Entwurf = { id: string; name: string; betreff: string; text: string };

const LEER: Entwurf = { id: "", name: "", betreff: "", text: "" };

type Props = { startArt?: MailVorlageArt; onClose: () => void };

export default function MailvorlagenVerwaltung({ startArt = "invoice", onClose }: Props) {
  const stand = useMailVorlagen();
  const pflegen = useMailVorlagePflegen();
  const loeschen = useMailVorlageLoeschen();
  const standard = useMailVorlageStandard();
  const [art, setArt] = useState<MailVorlageArt>(startArt);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [meldung, setMeldung] = useState("");
  const textfeld = useRef<HTMLTextAreaElement>(null!);

  useEffect(() => { setEntwurf(null); setMeldung(""); }, [art]);
  useEffect(() => {
    const taste = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [onClose]);

  const liste = stand.data?.listen?.[art] || [];
  const platzhalter = stand.data?.platzhalter || [];

  const oeffnen = (vorlage: MailVorlage) => {
    setMeldung("");
    setEntwurf(entwurf?.id === vorlage.id
      ? null
      : { id: vorlage.id, name: vorlage.name, betreff: vorlage.betreff, text: vorlage.text });
  };

  const platzhalterEinfuegen = (marke: string) => {
    if (!entwurf) return;
    const feld = textfeld.current;
    const stelle = feld ? feld.selectionStart : entwurf.text.length;
    const marke_ = `{${marke}}`;
    setEntwurf({ ...entwurf, text: entwurf.text.slice(0, stelle) + marke_ + entwurf.text.slice(stelle) });
    requestAnimationFrame(() => {
      feld?.focus();
      feld?.setSelectionRange(stelle + marke_.length, stelle + marke_.length);
    });
  };

  const speichern = async () => {
    if (!entwurf) return;
    try {
      await pflegen.mutateAsync({ ...entwurf, kind: art });
      setMeldung(`„${entwurf.name.trim()}" gespeichert.`);
      setEntwurf(null);
    } catch (fehler) {
      setMeldung(errorText(fehler));
    }
  };

  return (
    <div className="vg-bib-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="vg-bib" role="dialog" aria-modal="true" aria-label="Mailvorlagen">
        <div className="vg-bib-kopf">
          <div><p className="invoice-kicker">Anschreiben</p><h2>Mailvorlagen</h2></div>
          <button className="invoice-icon-button" type="button" onClick={onClose} aria-label="Schließen">×</button>
        </div>

        <div className="vg-bib-reiter" role="tablist">
          {ARTEN.map((eintrag) => (
            <button
              key={eintrag.id}
              type="button"
              role="tab"
              aria-selected={art === eintrag.id}
              className={art === eintrag.id ? "is-an" : ""}
              onClick={() => setArt(eintrag.id)}
            >
              {eintrag.titel}
              <b>{(stand.data?.listen?.[eintrag.id] || []).length}</b>
            </button>
          ))}
        </div>

        <p className="vg-bib-sinn">
          Die Vorlagen gelten für alle Inhaber. Name, Telefon und Adresse gehören nicht
          hierher — die Signatur des sendenden Postfachs hängt automatisch an
          (Einstellungen → Postfach). {"{anrede}"} wird zur Anrede aus dem Kundenstamm,
          etwa „Sehr geehrte Frau Müller“.
          Abschlag und Schlussrechnung teilen sich das Anschreiben der Rechnung.
          Die mit ★ markierte Vorlage belegt das Versandformular vor.
        </p>

        <div className="vg-bib-liste">
          {stand.isLoading && <p className="invoice-editor-hint">Vorlagen werden geladen …</p>}
          {stand.isError && <p className="invoice-inline-error">{errorText(stand.error)}</p>}
          {!stand.isLoading && liste.length === 0 && (
            <p className="invoice-editor-hint">
              Noch keine eigene Vorlage — bis dahin gilt der eingebaute Text.
            </p>
          )}
          {liste.map((vorlage) => (
            <div className={"vg-bib-eintrag" + (entwurf?.id === vorlage.id ? " is-offen" : "")} key={vorlage.id}>
              <div className="vg-bib-zeile">
                <button
                  type="button"
                  className={"vg-bib-stern" + (vorlage.ist_standard ? " is-an" : "")}
                  onClick={() => standard.mutate(vorlage.id)}
                  aria-pressed={vorlage.ist_standard}
                  title={vorlage.ist_standard ? "Ist die Vorgabe" : "Als Vorgabe setzen"}
                  disabled={standard.isPending}
                >
                  ★
                </button>
                <button type="button" className="vg-bib-oeffnen" onClick={() => oeffnen(vorlage)}>
                  <strong>{vorlage.name}</strong>
                  <small>{vorlage.betreff || "ohne Betreff"}</small>
                </button>
                <button
                  type="button"
                  className="vg-bib-loeschen"
                  onClick={() => loeschen.mutate(vorlage.id)}
                  disabled={loeschen.isPending}
                  aria-label={`${vorlage.name} löschen`}
                  title="Löschen"
                >
                  🗑
                </button>
              </div>

              {entwurf?.id === vorlage.id && (
                <VorlageForm
                  entwurf={entwurf}
                  setEntwurf={setEntwurf}
                  platzhalter={platzhalter}
                  onPlatzhalter={platzhalterEinfuegen}
                  textfeld={textfeld}
                />
              )}
            </div>
          ))}

          {entwurf && !entwurf.id && (
            <div className="vg-bib-eintrag is-offen">
              <div className="vg-bib-zeile">
                <span className="vg-bib-stern" aria-hidden="true">☆</span>
                <span className="vg-bib-oeffnen"><strong>Neue Vorlage</strong><small>noch nicht gespeichert</small></span>
              </div>
              <VorlageForm
                entwurf={entwurf}
                setEntwurf={setEntwurf}
                platzhalter={platzhalter}
                onPlatzhalter={platzhalterEinfuegen}
                textfeld={textfeld}
              />
            </div>
          )}
        </div>

        {meldung && <p className="invoice-inline-note" role="status">{meldung}</p>}

        <div className="vg-bib-fuss">
          <button
            className="invoice-button"
            type="button"
            onClick={() => { setEntwurf({ ...LEER }); setMeldung(""); }}
            disabled={Boolean(entwurf && !entwurf.id)}
          >
            + Neue Vorlage
          </button>
          <span className="vg-bib-stand">
            {entwurf ? "Vorlage in Arbeit" : `${liste.length} Vorlage${liste.length === 1 ? "" : "n"}`}
          </span>
          <button className="invoice-button" type="button" onClick={onClose}>Schließen</button>
          <button
            className="invoice-button primary"
            type="button"
            onClick={speichern}
            disabled={!entwurf || !entwurf.name.trim() || !entwurf.text.trim() || pflegen.isPending}
          >
            {pflegen.isPending ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VorlageForm({ entwurf, setEntwurf, platzhalter, onPlatzhalter, textfeld }: {
  entwurf: Entwurf;
  setEntwurf: (e: Entwurf) => void;
  platzhalter: string[];
  onPlatzhalter: (marke: string) => void;
  textfeld: React.RefObject<HTMLTextAreaElement>;
}) {
  return (
    <div className="vg-bib-form">
      <label className="invoice-editor-field">
        <span>Name</span>
        <input
          value={entwurf.name}
          onChange={(event) => setEntwurf({ ...entwurf, name: event.target.value })}
          placeholder="z. B. Erstangebot"
          autoFocus
        />
      </label>
      <label className="invoice-editor-field">
        <span>Betreff</span>
        <input
          value={entwurf.betreff}
          onChange={(event) => setEntwurf({ ...entwurf, betreff: event.target.value })}
          placeholder="Angebot {nummer} — Vimaana Garten"
        />
      </label>
      {/* Gespeichert wird nie der fertige Text, sondern der mit Platzhaltern —
          sonst ginge die zweite Mail mit dem Namen des ersten Kunden raus. */}
      <div className="vg-bib-platzhalter">
        {platzhalter.map((marke) => (
          <button type="button" key={marke} onClick={() => onPlatzhalter(marke)} title={`{${marke}} einfügen`}>
            {marke}
          </button>
        ))}
      </div>
      <textarea
        ref={textfeld}
        rows={8}
        value={entwurf.text}
        onChange={(event) => setEntwurf({ ...entwurf, text: event.target.value })}
        placeholder="Guten Tag {kunde},"
      />
    </div>
  );
}
